<template>
  <form class="ui-panel ui-form" novalidate @submit.prevent="save">
    <ui-banner v-if="errorCount" ref="banner" role="alert" tabindex="-1">{{
      rest || $t("delivery_team.form.errors", { count: errorCount })
    }}</ui-banner>

    <div class="ui-field">
      <label id="dt-role-label">{{ $t("role") }}</label>
      <ui-segment
        :value="form.type"
        :options="roleOptions"
        labelledby="dt-role-label"
        block
        @input="setType"
      />
    </div>

    <ui-section :title="$t('delivery_team.sections.contact')">
      <div class="ui-grid">
        <ui-field
          v-model="form.name"
          name="name"
          :label="$t('name')"
          :error="err('name')"
          required
          autocomplete="off"
        />
        <ui-field
          v-model="form.lastname"
          name="lastname"
          :label="$t('last_name')"
          :error="err('lastname')"
          required
          autocomplete="off"
        />
        <ui-field
          v-model="form.email"
          name="email"
          :label="$t('email_or_username')"
          :error="err('email')"
          :readonly="!isNew"
          :required="isNew"
          :hint="isNew ? '' : $t('delivery_team.form.email_locked')"
          full
          autocomplete="off"
        />
        <ui-field
          v-model="form.phone"
          name="phone"
          :label="$t('phone')"
          :error="err('phone')"
          type="tel"
          inputmode="tel"
          autocomplete="off"
        />
        <ui-field
          v-if="isNew || shell.isAdmin"
          v-model="form.password"
          name="password"
          :label="isNew ? $t('password') : $t('delivery_team.form.password_new')"
          :error="err('password')"
          type="password"
          :required="isNew"
          :hint="
            isNew
              ? $t('delivery_team.form.password_hint', { min: passwordMin })
              : $t('delivery_team.form.password_hint_edit')
          "
          autocomplete="new-password"
        />
      </div>
    </ui-section>

    <template v-if="courier">
      <ui-section :title="$t('delivery_team.sections.payment')">
        <div class="ui-grid">
          <ui-field
            v-model="form.bank"
            name="bank"
            :label="$t('bank_account')"
            :error="err('bank')"
            full
          />
          <ui-field
            v-model="form.ptype"
            name="ptype"
            :label="$t('courier_pay_type')"
            :error="err('ptype')"
            :options="payOptions"
          />
          <ui-field
            v-model="form.paying"
            name="paying"
            :label="`${$t('amount')} (${paySuffix})`"
            :error="err('paying')"
            inputmode="decimal"
          />
          <ui-field
            v-model="form.signed"
            name="signed"
            :label="$t('contract_signed_at')"
            :error="err('signed')"
            type="date"
          />
          <ui-field
            v-model="form.from"
            name="from"
            :label="$t('contract_active_from')"
            :error="err('from')"
            type="date"
            :hint="$t('contract_active_from_hint')"
          />
          <ui-field
            v-model="form.note"
            name="note"
            :label="$t('note')"
            :error="err('note')"
            full
          />
        </div>
      </ui-section>

      <ui-section :title="$t('delivery_team.sections.personal')">
        <div class="ui-grid">
          <ui-field
            v-model="form.dob"
            name="dob"
            :label="$t('date_of_birth')"
            :error="err('dob')"
            type="date"
          />
          <ui-field v-model="form.iban" name="iban" label="IBAN" :error="err('iban')" />
          <ui-field
            v-model="form.ecn"
            name="ecn"
            :label="$t('emergency_contact_name')"
            :error="err('ecn')"
          />
          <ui-field
            v-model="form.ecp"
            name="ecp"
            :label="$t('emergency_contact_phone')"
            :error="err('ecp')"
            type="tel"
            inputmode="tel"
          />
        </div>
      </ui-section>

      <ui-section :title="$t('delivery_team.sections.referrals')">
        <div class="ui-grid">
          <ui-field
            v-model="form.rurl"
            name="rurl"
            :label="$t('referral_url')"
            :error="err('rurl')"
            full
          />
          <ui-field
            v-model="form.rshort"
            name="rshort"
            :label="$t('referral_short_url')"
            :error="err('rshort')"
            :hint="$t('delivery_team.form.unique_hint')"
          />
          <ui-field
            v-model="form.rby"
            name="rby"
            :label="$t('referred_by')"
            :error="err('rby')"
            inputmode="numeric"
          />
        </div>
      </ui-section>
    </template>

    <div class="ui-form-actions">
      <ui-button :disabled="saving" @click="cancel">{{ $t("cancel") }}</ui-button>
      <ui-button type="submit" variant="primary" :disabled="saving">{{
        isNew ? $t("add_worker") : $t("save")
      }}</ui-button>
    </div>
  </form>
</template>

<script>
import {
  PASSWORD_MIN,
  PAYING_TYPES,
  emptyForm,
  formFromUser,
  validateForm,
} from "../teamLogic";
import hasTeamLabels from "../hasTeamLabels";

/**
 * Unos i izmjena korisnika u panelu: tri sekcije (kontakt, naplata i ugovor, lični podaci i preporuke).
 * Greška backenda (422) se prikazuje ispod polja na koje se odnosi, forma se ne zatvara i ništa se ne gubi.
 */
export default {
  name: "TeamMemberFormPanel",
  mixins: [hasTeamLabels],
  inject: ["reportShell"],
  props: { userId: { type: Number, default: 0 } },
  data() {
    const shell = this.reportShell;
    const user = this.userId ? shell.users.find((item) => item.id === this.userId) : null;
    return {
      form: user ? formFromUser(user) : emptyForm(),
      original: user,
      errors: {},
      rest: "",
      saving: false,
      passwordMin: PASSWORD_MIN,
    };
  },
  computed: {
    shell() {
      return this.reportShell;
    },
    isNew() {
      return !this.userId;
    },
    courier() {
      return this.form.type === "dostava";
    },
    errorCount() {
      return Object.keys(this.errors).length || (this.rest ? 1 : 0);
    },
    roleOptions() {
      return [
        { value: "dostava", label: this.$t("delivery_team.role.courier") },
        { value: "admin_delivery", label: this.$t("delivery_team.role.dispatcher") },
      ];
    },
    payOptions() {
      return [{ value: "", label: this.$t("delivery_team.form.not_selected") }].concat(
        PAYING_TYPES.map((value) => ({
          value: String(value),
          label: this.payTypeLabel(value),
        }))
      );
    },
    paySuffix() {
      return String(this.form.ptype) === "2" ? "%" : this.shell.currency;
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        // greška nestaje kad korisnik ispravi baš to polje
        Object.keys(this.errors).forEach((key) => {
          if (this.touched[key] !== this.form[key]) {
            this.$delete(this.errors, key);
          }
        });
      },
    },
  },
  created() {
    this.touched = {};
  },
  methods: {
    err(key) {
      const code = this.errors[key];
      if (!code) return "";
      const known = {
        required: this.$t("required_field"),
        password: this.$t("delivery_team.form.password_short", { min: PASSWORD_MIN }),
        number: this.$t("delivery_team.form.number"),
      };
      return known[code] || code;
    },
    setType(type) {
      this.form.type = type;
    },
    async save() {
      if (this.saving) return;
      this.rest = "";
      const local = validateForm(this.form, {
        isNew: this.isNew,
        isAdmin: this.shell.isAdmin,
      });
      if (Object.keys(local).length) {
        this.showErrors(local);
        return;
      }
      this.saving = true;
      try {
        const result = await this.shell.submitMember(this.form, this.original);
        if (result && result.fields) {
          this.rest = result.rest || "";
          this.showErrors(result.fields);
        }
      } finally {
        this.saving = false;
      }
    },
    showErrors(errors) {
      this.touched = {};
      Object.keys(errors).forEach((key) => {
        this.touched[key] = this.form[key];
      });
      this.errors = { ...errors };
      this.$nextTick(() => {
        const first = this.$el.querySelector("[aria-invalid='true']");
        if (first) first.focus();
        else if (this.$refs.banner) this.$refs.banner.$el.focus();
      });
    },
    cancel() {
      this.shell.cancelForm(this.userId);
    },
  },
};
</script>
